import { usePageSeo } from "@/hooks/usePageSeo";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { MapPin, ArrowRight, Plane, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import heroDestinationsImg from "@/assets/hero-destinations.jpg";

const airports = [
  { code: "PUJ", name: "Punta Cana International", city: "Punta Cana" },
  { code: "SDQ", name: "Las Américas International", city: "Santo Domingo" },
  { code: "STI", name: "Cibao International", city: "Santiago" },
  { code: "POP", name: "Gregorio Luperón", city: "Puerto Plata" },
];

const DestinationsPage = () => {
  usePageSeo("destinations");
  const { data: destinations = [], isLoading } = useQuery({
    queryKey: ["destinations-page"],
    queryFn: async () => {
      const { data: dests, error } = await supabase
        .from("destinations")
        .select("*")
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;

      const { data: tours } = await supabase
        .from("tours")
        .select("destination_id")
        .eq("is_active", true);

      const countMap: Record<string, number> = {};
      (tours || []).forEach((t: any) => {
        if (t.destination_id) countMap[t.destination_id] = (countMap[t.destination_id] || 0) + 1;
      });

      return (dests || []).map((d: any) => ({ ...d, tourCount: countMap[d.id] || 0 }));
    },
  });

  return (
    <main className="min-h-screen bg-background">
      <Navbar />
      
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <img src={heroDestinationsImg} alt="Destinations in Dominican Republic" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-white/75" />
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl mx-auto text-center">
            <p className="text-primary font-semibold tracking-wide uppercase text-sm mb-3">Dominican Republic</p>
            <h1 className="font-display text-4xl md:text-6xl font-bold text-foreground mb-6">
              Explore Our Destinations
            </h1>
            <p className="text-muted-foreground text-lg md:text-xl max-w-2xl mx-auto">
              From pristine beaches to historic cities, discover the diverse beauty of the Dominican Republic.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Destinations Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">Popular Destinations</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Discover the most beautiful locations in the Dominican Republic
            </p>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {destinations.map((dest: any, index: number) => (
                <motion.div
                  key={dest.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group relative rounded-lg overflow-hidden shadow-sm border border-border hover:shadow-md transition-all duration-300"
                >
                  <div className="relative h-72 overflow-hidden">
                    <img 
                      src={dest.main_image_url || "/placeholder.svg"} 
                      alt={dest.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                  </div>

                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="flex items-center gap-1 text-white/80 mb-2">
                      <MapPin className="w-4 h-4" />
                      <span className="text-sm font-medium">{dest.tourCount} Tours Available</span>
                    </div>
                    <h3 className="font-display text-2xl font-bold text-white mb-1">{dest.title}</h3>
                    <p className="text-white/70 text-sm mb-4">{dest.short_description}</p>
                    <Link to={`/destinations/${dest.slug}`}>
                      <Button size="sm" className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg">
                        Explore <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Airport Transfers */}
      <section className="py-20 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-primary/10 rounded-lg px-4 py-2 mb-4">
              <Plane className="w-4 h-4 text-primary" />
              <span className="text-primary text-sm font-medium">Airport Transfers</span>
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">We Cover All Major Airports</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">Reliable airport transfer service from all Dominican Republic airports</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {airports.map((airport, index) => (
              <motion.div key={airport.code} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}
                className="bg-card rounded-lg p-6 shadow-sm hover:shadow-md transition-all border border-border">
                <div className="w-14 h-14 rounded-lg bg-primary flex items-center justify-center mb-4">
                  <span className="text-primary-foreground font-display font-bold text-lg">{airport.code}</span>
                </div>
                <h3 className="font-display font-bold text-card-foreground mb-1">{airport.city}</h3>
                <p className="text-muted-foreground text-sm">{airport.name}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link to="/transfers">
              <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg px-8">
                Book Airport Transfer <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary relative overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-display text-3xl md:text-5xl font-bold text-primary-foreground mb-6">
              Your Gateway to Paradise
            </h2>
            <p className="text-primary-foreground/80 text-lg mb-8 max-w-2xl mx-auto">
              Whether you're seeking relaxation on pristine beaches, adventure in nature, or exploring rich history — we'll take you there.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/tours"><Button size="lg" className="bg-white text-primary hover:bg-white/90 rounded-lg px-8">Browse Tours</Button></Link>
              <Link to="/contact"><Button size="lg" variant="outline" className="border-white/40 text-primary-foreground hover:bg-white/10 rounded-lg px-8 text-gray-950">Contact Us</Button></Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default DestinationsPage;
